import React, { FC } from 'react';
import {
  Badge,
  GenNavTile,
  UniversalLink,
  GenInternalLink,
} from '@/components';
import {
  TypeAccountNavTileFields,
  TypeBadgeFields,
} from '@/core/generated-types';
import {
  TypeChildRecord,
  TypeUserRecord,
} from '@/lib/api/accountManager/types';
import { TypeSubscriptionRecord } from '@/lib/api/subscriptionManager/types';
import { isSubscriptionActive } from '@/lib/api/subscriptionManager/helpers/subscriptionHelpers';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';
import { DisplayDateFormat } from '@/utils/dates/constants';
import { formatDateForDisplay } from '@/utils/dates/dateFormatters';
import { getCtaTextStyles } from '@/components/utils';

export type FamilyProfileTileProps = {
  t: TypeAccountSettingsPageTranslations;
  user: TypeUserRecord;
  subscriptions: TypeSubscriptionRecord[] | undefined;
  tile: TypeAccountNavTileFields;
  locale: string | undefined;
  subscriberBadge: TypeBadgeFields;
};

type ChildDisplayProps = {
  child: TypeChildRecord;
};

const FamilyProfileTile: FC<FamilyProfileTileProps> = ({
  t,
  user,
  subscriptions,
  tile,
  locale,
  subscriberBadge,
}) => {
  const ChildDisplay: FC<ChildDisplayProps> = ({ child }) => {
    if (child.is_hidden) {
      return null;
    }

    const birthDate = formatDateForDisplay(
      child.birthday,
      locale,
      DisplayDateFormat.NumericMonthDayYear,
    );
    const childHasSubscription = subscriptions?.some(
      (subscription) =>
        isSubscriptionActive(subscription) &&
        subscription.child_uuid === child.uuid,
    );

    function _generateEditChildLink(childUuid: string) {
      return `/account/settings/edit-child/${childUuid}`;
    }

    return (
      <div className="mb-6">
        {childHasSubscription && (
          <div className="mb-3" data-testid="section-SubscriberBadge">
            <Badge {...subscriberBadge} />
          </div>
        )}
        <div
          className="flex items-start justify-between"
          data-testid="section-childInfo"
        >
          <div>
            <div
              className="mb-2 flex flex-row gap-4"
              data-testid={`name-childName-${child.name}`}
            >
              <p className="font-bold">{t.nameLabel}</p>
              <p>{child.name}</p>
            </div>
            <div
              className="mb-2 flex flex-row gap-4"
              data-testid={`date-childBirthDate-${child.name}`}
            >
              <p className="font-bold">{t.dateOfBirthLabel}</p>
              <p>{birthDate}</p>
            </div>
          </div>
          <GenInternalLink
            cta={t.editCta}
            href={_generateEditChildLink(child.uuid)}
            classes="mb-4"
            testId={`link-editChildLink-${child.name}`}
          />
        </div>
      </div>
    );
  };

  return (
    <GenNavTile
      title={tile.title}
      subTitle={tile.subheader}
      description={tile.description}
    >
      {user.children?.map((child) => (
        <ChildDisplay child={child} key={child.uuid} />
      ))}
      <UniversalLink
        link={tile.buttonLink}
        classes={`font-bold ${getCtaTextStyles()}`}
      />
    </GenNavTile>
  );
};

export default React.memo(FamilyProfileTile);
